import React, { ReactNode, useCallback, useMemo, useRef, useState } from 'react'

import {
  AutocompleteRenderGetTagProps,
  PaperProps,
  Popper,
  PopperProps,
  SxProps
} from '@mui/material'
import { Color } from 'themes'
import { Severity } from 'utils/types'

import { useTranslation } from 'language/translator'

/* Atoms */
import Autocomplete, {
  RenderInputParams
} from 'components_new/atoms/Autocomplete'
import { getAvatarSize } from 'components_new/atoms/Avatar'
import Box from 'components_new/atoms/Box'
import Chip from 'components_new/atoms/Chip'
import Divider from 'components_new/atoms/Divider'
import Icon, { IconName } from 'components_new/atoms/Icon'
import ListItemAvatar from 'components_new/atoms/List/ListItemAvatar'
import ListItemText from 'components_new/atoms/List/ListItemText'
import MenuItem from 'components_new/atoms/Menu/MenuItem'
import Paper from 'components_new/atoms/Paper'
import Text from 'components_new/atoms/Text'
import TextField from 'components_new/atoms/TextField'
import Tooltip from 'components_new/atoms/Tooltip'

/* Molecules */
import GenericAvatar, {
  GenericAvatarProps
} from 'components_new/molecules/GenericAvatar'
import IconTip from 'components_new/molecules/IconTip'

import { sortAlphabeticalAsc } from 'utils/sortHelper'

export type Option = {
  avatar?: GenericAvatarProps['variant']
  body?: string
  chip?: string // TODO: remove?
  endAdornment?: ReactNode
  color?: Color | Severity
  disabled?: boolean
  disabledTitle?: ReactNode
  group?: string
  icon?: IconName
  id: string | number | null
  title: string
}

/**
 * Value type for {@link SelectSearch} controlled `selected`.
 *
 * @typeParam OptionType - Concrete option shape (extends {@link Option}).
 * @typeParam Multiple - If `true` (default), the value is an array;
 * otherwise a single option or `null`.
 */
type SelectValue<
  OptionType extends Option,
  Multiple extends boolean
> = Multiple extends true ? OptionType[] : OptionType | null

/**
 * Value type passed to {@link SelectSearchProps.onChange | onChange}.
 *
 * @typeParam OptionType - Concrete option shape (extends {@link Option}).
 * @typeParam Multiple - Matches `multiple` prop.
 * @typeParam DisableClearable - When single-select and `true`, `null`
 * is never emitted.
 */
type SelectOnChangeValue<
  OptionType extends Option,
  Multiple extends boolean,
  DisableClearable extends boolean
> = Multiple extends true
  ? OptionType[]
  : DisableClearable extends true
  ? OptionType
  : OptionType | null

interface SelectSearchProps<
  OptionType extends Option,
  Multiple extends boolean = true,
  DisableClearable extends boolean = false
> {
  actions?: ReactNode
  clearableOption?: boolean
  closeOnSelect?: boolean
  color?: 'primary' | 'secondary'
  /** Replaces the field's own icon, e.g. with a menu button of its own. */
  customIcon?: ReactNode
  disableClearable?: DisableClearable
  disabled?: boolean
  error?: boolean
  externalLabel?: string
  fullWidth?: boolean
  helperText?: ReactNode
  keepOnSelect?: boolean
  label?: string
  lastOption?: ReactNode
  multiple?: Multiple
  noGutter?: boolean
  onChange: (
    value: SelectOnChangeValue<OptionType, Multiple, DisableClearable>
  ) => void
  options: OptionType[]
  placeholder?: string
  PopperWidth?: number
  selected: SelectValue<OptionType, Multiple>
  size?: 'small' | 'medium'
  sort?: boolean
  sx?: SxProps
}

function OptionItem<OptionType extends Option>(props: { option: OptionType }) {
  const { option } = props

  const textRef = useRef<HTMLSpanElement>(null)
  const [showTooltip, setShowTooltip] = useState(false)

  const handleMouseEnter = useCallback(() => {
    const el = textRef.current

    if (el && el.scrollWidth > el.clientWidth) {
      setShowTooltip(true)
    }
  }, [])

  const handleMouseLeave = useCallback(() => {
    setShowTooltip(false)
  }, [])

  return (
    <>
      {option.icon ? (
        <Icon
          name={option.icon}
          fontSize="small"
          sx={{ mr: 1, color: 'text.secondary' }}
        />
      ) : null}
      {option.avatar ? (
        <ListItemAvatar sx={{ minWidth: getAvatarSize('small').width * 1.5 }}>
          <GenericAvatar
            body={option.body}
            color={option.color}
            size="small"
            title={option.title}
            variant={option.avatar}
          />
        </ListItemAvatar>
      ) : null}
      <Tooltip title={option.title} open={showTooltip} placement="top">
        <Box
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          sx={{ overflow: 'hidden', flex: 1, minWidth: 0 }}
        >
          <ListItemText
            primary={option.title}
            primaryTypographyProps={{
              noWrap: true,
              ref: textRef
            }}
            secondary={option.body}
            secondaryTypographyProps={{ color: 'text.secondary' }}
          />
        </Box>
      </Tooltip>
      {option.chip ? (
        <Chip size="small" label={option.chip} color="secondary" />
      ) : null}
      {option.endAdornment ?? null}
      {option.disabled ? (
        <IconTip title={option.disabledTitle} placement="left" />
      ) : null}
    </>
  )
}

export const getSortedOptions = (
  options: Option[],
  showGroups: boolean
): Option[] => {
  return [...options].sort((a, b) => {
    if (showGroups && a.group && b.group) {
      if (a.group.toLowerCase() === b.group.toLowerCase()) {
        return sortAlphabeticalAsc(a.title, b.title)
      }

      return sortAlphabeticalAsc(a.group, b.group)
    }

    return sortAlphabeticalAsc(a.title, b.title)
  })
}

const MemoizedIcon = React.memo(
  ({ keepInputValue }: { keepInputValue: boolean }) => (
    <Icon
      className="CheckBoxIcon"
      color={keepInputValue ? 'primary' : 'disabled'}
      name={keepInputValue ? 'CheckBox' : 'CheckBoxOutlineBlank'}
      fontSize="small"
      sx={{
        display: 'inline-block',
        verticalAlign: '-0.325rem',
        mr: '0.25rem'
      }}
    />
  )
)

const MemoizedText = React.memo(
  ({ keepInputValue }: { keepInputValue: boolean }) => (
    <Text
      className="CheckBoxLabel"
      color={keepInputValue ? 'text.primary' : 'text.disabled'}
      variant="body2"
      sx={{
        display: 'inline-block',
        mr: '0.25rem'
      }}
    >
      Behåll sökning vid val
    </Text>
  )
)

const MemoizedIconTip = React.memo(() => (
  <IconTip
    sx={{ color: 'action.disabled' }}
    title="Markera för att låta sökfrasen stanna kvar i fältet efter varje val, så att du kan välja flera alternativ utan att behöva skriva om samma sökord."
  />
))

const MemoizedPaperComponent = React.memo(
  ({
    children,
    keepInputValue,
    keepOnSelect,
    lastOption,
    multiple,
    setKeepInputValue
  }: {
    children: ReactNode
    keepInputValue: boolean
    keepOnSelect: boolean
    lastOption: ReactNode
    multiple: boolean
    setKeepInputValue: (val: boolean) => void
  }) => {
    return (
      <Paper sx={{ overflow: 'hidden' }}>
        {multiple && keepOnSelect ? (
          <>
            <Box
              onClick={() => setKeepInputValue(!keepInputValue)}
              onMouseDown={(e) => {
                e.stopPropagation()
                e.preventDefault()
              }}
              sx={{
                cursor: 'pointer',
                gap: 0.5,
                p: 1
              }}
            >
              <MemoizedIcon keepInputValue={keepInputValue} />
              <MemoizedText keepInputValue={keepInputValue} />
              <MemoizedIconTip />
            </Box>
            <Divider />
          </>
        ) : null}
        {children}
        {lastOption ? (
          <Box
            onMouseDown={(e) => {
              e.stopPropagation()
              e.preventDefault()
            }}
          >
            {lastOption}
          </Box>
        ) : null}
      </Paper>
    )
  }
)

const MemoizedPopperComponent = React.memo(
  ({
    popperProps,
    PopperWidth
  }: {
    popperProps: PopperProps
    PopperWidth?: number
  }) => (
    <Popper
      {...popperProps}
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, 3]
          }
        }
      ]}
      placement="bottom-start"
      sx={PopperWidth ? { width: `${PopperWidth}px!important` } : null}
    />
  )
)

/**
 * SelectSearch – typed wrapper around MUI `Autocomplete`
 * with grouping, avatars, and
 * an optional sticky search helper for rapid multi-selection.
 *
 *
 * @typeParam OptionType - Concrete option shape (extends {@link Option}).
 * @typeParam Multiple - Multi-select mode (default `true`).
 * @typeParam DisableClearable - Single-select only: when `true`,
 * value cannot be cleared.
 *
 * @param props - See {@link SelectSearchProps}.
 * @returns A fully controlled autocomplete component.
 */
const SelectSearchComponent = <
  OptionType extends Option,
  Multiple extends boolean = true,
  DisableClearable extends boolean = false
>(
    props: SelectSearchProps<OptionType, Multiple, DisableClearable>,
    ref: React.ForwardedRef<HTMLDivElement>
  ) => {
  const [inputValue, setInputValue] = useState<string>('')
  const [keepInputValue, setKeepInputValue] = useState<boolean>(false)
  const [hasValue, setHasValue] = useState<boolean>(false)

  const { t } = useTranslation()

  const setKeepInputValueCallback = useCallback((val: boolean) => {
    setKeepInputValue(val)
  }, [])

  const handleChange = useCallback(
    (
      _event: React.SyntheticEvent,
      newValue: SelectOnChangeValue<OptionType, Multiple, DisableClearable>
    ) => {
      props.onChange(newValue)
      setHasValue(
        Array.isArray(newValue) ? newValue.length > 0 : Boolean(newValue)
      )
    },
    [props.onChange]
  )

  const handleInputChange = useCallback(
    (event: React.SyntheticEvent, value: any, reason: string) => {
      if (keepInputValue) {
        if (event && event.type === 'blur') {
          setInputValue('')
        } else if (reason !== 'reset') {
          setInputValue(value ?? '')
        }
      } else {
        setInputValue(value ?? '')
      }
    },
    [keepInputValue]
  )

  const renderTags = useCallback(
    (value: OptionType[], getTagProps: AutocompleteRenderGetTagProps) =>
      value.map((option, index) => (
        <Chip
          avatar={
            option.avatar ? (
              <GenericAvatar
                body={option.body}
                color={option.color}
                size="xsmall"
                title={option.title}
                variant={option.avatar}
              />
            ) : undefined
          }
          color={props.color ?? undefined}
          label={option.title}
          size={props.size ?? 'small'}
          sx={{
            maxWidth: 'calc(100% - 36px)'
          }}
          {...getTagProps({ index })}
          key={index}
        />
      )),
    [props.color, props.size]
  )

  const renderInput = useCallback(
    (params: RenderInputParams) => (
      <TextField
        {...params}
        error={props.error}
        helperText={props.helperText}
        InputProps={{
          ...params.InputProps,
          endAdornment: props.customIcon ? (
            <>
              {params.InputProps.endAdornment}
              {props.customIcon}
            </>
          ) : (
            params.InputProps.endAdornment
          ),
          startAdornment: props.multiple ? (
            params.InputProps.startAdornment
          ) : props.selected &&
            !Array.isArray(props.selected) &&
            props.selected.avatar ? (
                <Box sx={{ px: 0.5 }}>
                  <GenericAvatar
                    body={props.selected.body}
                    color={props.selected.color}
                    size="xsmall"
                    title={props.selected.title}
                    variant={props.selected.avatar}
                  />
                </Box>
              ) : undefined
        }}
        label={props.label}
        noGutter={true}
        placeholder={props.placeholder ?? `${t('global', 'search')}...`}
        sx={{
          '& .MuiFilledInput-root': {
            pt: hasValue ? '22px' : undefined,
            pb: hasValue ? '8px' : undefined
          }
        }}
        variant="filled"
      />
    ),
    [
      props.customIcon,
      props.helperText,
      props.multiple,
      props.selected,
      props.label,
      props.placeholder,
      hasValue
    ]
  )

  const clearableOption = props.clearableOption

  const renderOption = useCallback(
    (
      props: React.HTMLAttributes<HTMLLIElement> & {
        key: any
      },
      option: OptionType
    ) =>
      option.id == null && clearableOption ? (
        <div key={'null'}>
          <Box
            sx={{ borderBottom: '1px solid', borderColor: 'divider', py: 0.5 }}
          >
            <MenuItem {...props} component="div">
              {t('global', 'none')}
            </MenuItem>
          </Box>
        </div>
      ) : (
        <div key={option.id}>
          <MenuItem
            {...props}
            component="div"
            disabled={option.disabled}
            sx={{
              height: '100%',
              '&.Mui-focused': {
                borderLeft: '3px solid',
                borderColor: 'primary.main',
                pl: 'calc(16px - 3px)'
              },

              // grouped options inset their content under the group
              // header, while the focus border stays at the edge
              '.Mui-grouped-option &': {
                pl: 4
              },
              '.Mui-grouped-option &.Mui-focused': {
                pl: 'calc(32px - 3px)'
              }
            }}
          >
            <OptionItem option={option} />
          </MenuItem>
        </div>
      ),
    []
  )

  const getOptionLabel = useCallback((option: Option) => `${option.title}`, [])

  const showGroups = useMemo(
    () =>
      Boolean(
        props.options[0]?.group &&
          !props.options.every(({ group }) => group === props.options[0].group)
      ),
    [props.options]
  )
  const getGroupBy = useCallback(
    (option: Option) => (showGroups && option.group ? option.group : ''),
    [showGroups]
  )
  const isOptionEqualToValue = useCallback(
    (option: Option, value: Option) => option.id === value.id,
    []
  )

  const onClose = useCallback(() => setInputValue(''), [])

  const sortedOptions = useMemo(() => {
    const options = props.sort
      ? getSortedOptions(props.options, showGroups)
      : props.options

    return [
      ...(props.clearableOption && options.length > 0
        ? [{ id: null, title: t('global', 'none') }]
        : []),
      ...options
    ]
  }, [props.sort, props.options, showGroups])

  const PaperComponent = useMemo(
    () => (paperProps: PaperProps) =>
      (
        <MemoizedPaperComponent
          keepInputValue={keepInputValue}
          keepOnSelect={!!props.keepOnSelect}
          lastOption={props.lastOption}
          multiple={!!props.multiple}
          setKeepInputValue={setKeepInputValueCallback}
        >
          {paperProps.children}
        </MemoizedPaperComponent>
      ),
    [props.keepOnSelect, props.lastOption, props.multiple]
  )

  const PopperComponent = useMemo(
    () => (popperProps: PopperProps) =>
      (
        <MemoizedPopperComponent
          popperProps={popperProps}
          PopperWidth={props.PopperWidth}
        />
      ),
    [props.PopperWidth]
  )

  const autocompleteSx = useMemo(
    () => ({
      width: '100%',
      maxWidth: '100%',
      minWidth: 0,
      mb: props.noGutter ? 0 : 1,
      ...props.sx
    }),
    [props.noGutter, props.sx]
  )

  return (
    <>
      {props.externalLabel || props.actions ? (
        <Box
          sx={{
            alignItems: 'flex-end',
            display: 'flex',
            justifyContent: 'space-between',
            mb: 0.25
          }}
        >
          <Text
            color={props.disabled ? 'text.disabled' : 'text.primary'}
            fontWeight="medium"
            variant="body2"
          >
            {props.externalLabel}
          </Text>

          {props.actions}
        </Box>
      ) : null}
      <Autocomplete
        autoHighlight={true}
        disableClearable={
          Boolean(props.disableClearable) || Boolean(props.clearableOption)
        }
        disableCloseOnSelect={!props.closeOnSelect}
        disabled={props.disabled}
        filterSelectedOptions={props.multiple}
        fullWidth={props.fullWidth}
        getOptionLabel={getOptionLabel}
        groupBy={getGroupBy}
        isOptionEqualToValue={isOptionEqualToValue}
        ListboxItemHeight={props.options[0]?.body ? 56 : 40} // @TODO?
        ListboxProps={{
          sx: {
            maxHeight: 256
          }
        }}
        multiple={props.multiple}
        onChange={handleChange}
        onInputChange={handleInputChange}
        onClose={onClose}
        openOnFocus={true}
        options={sortedOptions}
        PaperComponent={PaperComponent}
        PopperComponent={PopperComponent}
        ref={ref}
        renderTags={renderTags}
        inputValue={inputValue}
        renderInput={renderInput}
        // The custom icon stands in for the field's own, rather than beside it.
        forcePopupIcon={props.customIcon ? false : undefined}
        popupIcon={<Icon name={props.multiple ? 'Add' : 'UnfoldMore'} />}
        renderOption={renderOption}
        size={props.size ?? 'small'}
        sx={autocompleteSx}
        value={props.selected}
      />
    </>
  )
}

SelectSearchComponent.displayName = 'SelectSearch'

/**
 * ForwardRef wrapper for {@link SelectSearchComponent}.
 *
 * @remarks
 * Exported as the memoized default;
 * most consumers should import `SelectSearch`.
 */
const ForwardedSelectSearch = React.forwardRef(SelectSearchComponent) as <
  OptionType extends Option,
  Multiple extends boolean = true,
  DisableClearable extends boolean = false
>(
  props: SelectSearchProps<OptionType, Multiple, DisableClearable> & {
    ref?: React.ForwardedRef<HTMLDivElement>
  }
) => React.JSX.Element

/** Memoized export to reduce re-renders in complex forms. */
const SelectSearch = React.memo(
  ForwardedSelectSearch
) as typeof ForwardedSelectSearch

export default SelectSearch
